import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Container,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import apiClient from '../../api/apiClient';
import { extractErrorMessage } from '../../api/extractErrorMessage';
import DWLFLoader from '../../components/DWLFLoader';
import { describeStoredWindow, describeWeeklyStage, anyUnverified } from './cycleScreenerFormat';
import DepthBadge from './DepthBadge';

/*
 * Cycle Setup Screener — "where should I look today".
 *
 * Universe-wide cross-reference of the weekly cycle timing window with
 * daily cycle confirmation, ranked best-first. A confirmed_setup row is
 * the multi-timeframe pattern: the higher timeframe says a low is DUE,
 * and the lower timeframe says it has ARRIVED.
 */

const MONO = '"Space Mono", monospace';
const GROTESK = '"Space Grotesk", sans-serif';
const MUTED = 'var(--dwlf-ink-50)';

const STAGES = {
  confirmed_setup: {
    label: 'Confirmed setup',
    color: 'success',
    info: 'The multi-timeframe trigger has fired: the weekly timing window is open AND a daily cycle pivot confirmed within the last 15 days. After 15 days the confirmation goes stale and the row decays back to Window open.',
  },
  awaiting_confirmation: {
    label: 'Awaiting confirmation',
    color: 'warning',
    info: 'A provisional daily pivot is forming inside an open weekly window — a candidate, not a trigger. It either confirms (→ Confirmed setup) or breaks (→ back to Window open). Forming daily pivots are deliberately not persisted to the chart or events feed, so this screener is the only place they are visible.',
  },
  window_open: {
    label: 'Window open',
    color: 'default',
    info: 'The weekly timing window is open but no daily signal exists yet. Timing context only — nothing to act on until a daily pivot appears.',
  },
};

// The stored window's stages (indicators 4.0.0): `earliest` is before the
// window opens, `missed` past its hard max with no weekly pivot — a stand-down.
const WINDOW_LABEL = { earliest: 'EARLIEST', early: 'EARLY', mid: 'MID', late: 'LATE', missed: 'MISSED' };

// Formatting of the stored window lives in ./cycleScreenerFormat (pure, tested).

// "From pivot" renders green within this band — referenced by the cell
// styling, the column tooltip, and the footer, so they can't drift apart.
const PIVOT_GREEN_PCT = 4;

// Column explanations, surfaced as info icons. Multi-paragraph strings render
// via whiteSpace: 'pre-line' in the tooltip.
const COLUMN_INFO = {
  stage: 'Ranked best-first: Confirmed setup → Awaiting confirmation → Window open. Ties break by deeper weekly window, then fresher daily confirmation, then symbol (alphabetical).\n\nHover each stage chip for what that stage means.',
  weeklyWindow: 'Where this symbol is in its weekly cycle — the same window the chart draws, read from the window store.\n\nLine 1: the stage, and how long since the last weekly pivot. EARLY = past the 25th percentile of this symbol’s own pivot-to-pivot gaps, MID = past the middle, LATE = past the 75th. Deeper ranks higher. Only these three appear here: before the 25th percentile a window is not yet a setup, and past its last chance it is a stand-down.\n\nLine 2: the window itself — the shaded blue band on the chart, 25th to 75th percentile. Half of this symbol’s past weekly pivots landed inside it. Then where today sits against it.\n\nLine 3: the last chance — the longest gap on record. Past it with no weekly pivot the window counts as missed: a stand-down, so the row leaves the screener. “From N past cycles” is how many pivot-to-pivot gaps the window is built from: fewer means a less certain window.',
  dailyCycle: 'The lower-timeframe trigger: the state of the current daily cycle pivot, its freshness, and its evidence score.\n\nThe % is points scored out of 155 across ~12 independent checks at the pivot — DSS oversold/overbought depth (up to 40), DSS cross (20), swing pattern (15), 10-bar SMA reclaim + hold (15 + 15), rhythm vs the symbol’s own cycle length (up to 20), lower-timeframe assists, volume spike (10).\n\nConfirmed pivots typically score 40–70%. It is a quality ranking of the pivot’s evidence, not a probability — and unrelated to the weekly window position.',
  depth: 'How far price has moved from the cycle extreme, against the moves past cycles made into this window (long: the fall from the weekly high; short: the rise from the weekly low).\n\nIN ZONE = inside the typical range (25th–75th percentile of past cycles). Shallow / Deep = past the smallest / typical move. Not reached = short of the smallest move on record, so the band does not apply yet.\n\nHistorical context, not a target: it does not change the stage or the ranking. Hover a badge for the numbers.',
  pivot: 'The daily cycle pivot price the setup is anchored to. Natural invalidation: a close through this level kills the pivot (and any tight stop beyond it).',
  last: 'Latest stored daily close — can lag the live session by a day.',
  fromPivot: `The confirmation tax: how far price has already moved off the pivot by the time you see the row. Smaller = fresher entry, tighter stop-to-pivot distance, better R:R. Green within ±${PIVOT_GREEN_PCT}%.`,
};

const PAGE_INFO = 'The model: a higher-timeframe timing window crossed with a lower-timeframe confirmation. Long side = weekly low window open × daily cycle low; the short side mirrors it with highs.\n\nReads the persistent cycle FSM state directly — exactly what the cycle engine itself sees, recomputed on load.\n\nThe long/short counts alone summarise the universe regime (e.g. far more shorts than longs = late-cycle topping).';

const headCell = { fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, fontWeight: 600 };

const infoTooltipSlotProps = {
  tooltip: { sx: { maxWidth: 380, fontSize: '0.75rem', lineHeight: 1.5, whiteSpace: 'pre-line' } },
};

const InfoTip = ({ info, size = 14 }) => (
  <Tooltip title={info} placement="top" enterTouchDelay={0} leaveTouchDelay={5000} slotProps={infoTooltipSlotProps}>
    <InfoOutlinedIcon
      tabIndex={0}
      role="button"
      aria-label="More info"
      sx={{ fontSize: size, color: 'var(--dwlf-ink-35)', verticalAlign: 'middle', '&:hover': { color: 'var(--dwlf-ink-70)' }, '&:focus-visible': { color: 'var(--dwlf-ink-70)', outline: 'none' } }}
    />
  </Tooltip>
);

const HeadLabel = ({ label, info, align }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, width: '100%', justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
    {label}
    <InfoTip info={info} />
  </Box>
);

// Rows leave "unverified" out, so the page says it once whenever any row is.
const UnverifiedNote = ({ rows }) => (anyUnverified(rows) ? (
  <Typography sx={{ fontFamily: MONO, fontSize: '0.75rem', color: MUTED, mb: 1.5 }}>
    Weekly windows are unverified unless a row says otherwise: treat window timing as a guide, not a measured edge.
  </Typography>
) : null);

const ScreenerFooter = () => (
  <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'var(--dwlf-ink-35)', mt: 3 }}>
    Window = how far the symbol is through its own historical cycle-length distribution (early ≥25th percentile,
    mid ≥50th, late ≥75th). Confirmed setups rank first, then deeper windows, then fresher confirmations.
    "From pivot" is the entry-freshness number — green within ±{PIVOT_GREEN_PCT}%.
  </Typography>
);

const NoSetups = ({ side }) => (
  <Paper sx={{ p: 4, textAlign: 'center' }}>
    <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED }}>
      No {side} setups — no symbol currently has its weekly {side === 'long' ? 'low' : 'high'} window open.
    </Typography>
  </Paper>
);

const WeeklyWindowCell = ({ weekly, side, nowMs }) => (
  <>
    <Typography variant="body2" sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>
      {describeWeeklyStage(WINDOW_LABEL[weekly.window] || weekly.window, weekly.daysSincePivot, side)}
    </Typography>
    {describeStoredWindow(weekly.timing, nowMs).map((line) => (
      <Typography key={line} variant="body2" sx={{ fontFamily: MONO, fontSize: '0.72rem', color: MUTED }}>
        {line}
      </Typography>
    ))}
  </>
);

const ScreenerHead = () => (
  <TableHead>
    <TableRow>
      <TableCell sx={headCell}>Symbol</TableCell>
      <TableCell sx={headCell}><HeadLabel label="Stage" info={COLUMN_INFO.stage} /></TableCell>
      <TableCell sx={headCell}><HeadLabel label="Weekly window" info={COLUMN_INFO.weeklyWindow} /></TableCell>
      <TableCell sx={headCell}><HeadLabel label="Depth" info={COLUMN_INFO.depth} /></TableCell>
      <TableCell sx={headCell}><HeadLabel label="Daily cycle" info={COLUMN_INFO.dailyCycle} /></TableCell>
      <TableCell sx={headCell} align="right"><HeadLabel label="Pivot" info={COLUMN_INFO.pivot} align="right" /></TableCell>
      <TableCell sx={headCell} align="right"><HeadLabel label="Last" info={COLUMN_INFO.last} align="right" /></TableCell>
      <TableCell sx={headCell} align="right"><HeadLabel label="From pivot" info={COLUMN_INFO.fromPivot} align="right" /></TableCell>
    </TableRow>
  </TableHead>
);

// A long setup sits in a weekly LOW window (its depth is the fall), a short one in a HIGH window (the rise).
const WINDOW_SIDE = { long: 'low', short: 'high' };

const fmtPrice = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));

const CycleScreenerPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [side, setSide] = useState('long');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      // depth=1: each setup's weekly depth band (DWLF-416).
      .get('/screener/cycle-setups?depth=1')
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(extractErrorMessage(e, 'Failed to load cycle setups'));
      });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return <Box p={3}><Alert severity="error">{error}</Alert></Box>;
  }
  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="large" />
      </Box>
    );
  }

  const rows = data[side] || [];
  const nowMs = Date.now();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={2}>
        <Typography sx={{ fontFamily: GROTESK, fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 600, letterSpacing: '-0.03em', color: 'var(--dwlf-text)', display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          Cycle Setup Screener
          <InfoTip info={PAGE_INFO} size={18} />
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED }}>
          Weekly timing window × daily confirmation, universe-wide — ranked best-first
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'var(--dwlf-ink-10)', mb: 2 }}>
        <Tabs
          value={side}
          onChange={(_e, v) => setSide(v)}
          sx={{ '& .MuiTab-root': { fontFamily: MONO, fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase' } }}
        >
          <Tab label={`Long setups (${(data.long || []).length})`} value="long" />
          <Tab label={`Short setups (${(data.short || []).length})`} value="short" />
        </Tabs>
      </Box>

      <UnverifiedNote rows={rows} />

      {rows.length === 0 ? (
        <NoSetups side={side} />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <ScreenerHead />
            <TableBody>
              {rows.map((s) => {
                const stage = STAGES[s.stage] || { label: s.stage, color: 'default' };
                return (
                  <TableRow
                    key={`${s.symbol}-${s.stage}`}
                    onClick={() => navigate(`/markets/${s.symbol.replace(/\//g, '-')}`)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'background.surface' } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{s.symbol}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={stage.info || ''} placement="top" enterTouchDelay={0} leaveTouchDelay={5000} slotProps={infoTooltipSlotProps}>
                        <Chip
                          label={stage.label}
                          size="small"
                          color={stage.color}
                          sx={{ fontWeight: 600 }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell><WeeklyWindowCell weekly={s.weekly} side={side} nowMs={nowMs} /></TableCell>
                    <TableCell>
                      <Box component="span" onClick={(e) => e.stopPropagation()}><DepthBadge depth={s.depth} side={WINDOW_SIDE[side]} /></Box>
                    </TableCell>
                    <TableCell>
                      {s.daily?.status === 'confirmed' && (
                        <Typography variant="body2" sx={{ fontFamily: MONO, fontSize: '0.8rem', color: '#22c55e' }}>
                          Confirmed {s.daily.daysSinceConfirm}d ago
                          {Number.isFinite(s.daily.confidencePct) ? ` · ${Math.round(s.daily.confidencePct)}%` : ''}
                        </Typography>
                      )}
                      {s.daily?.status === 'provisional' && (
                        <Typography variant="body2" sx={{ fontFamily: MONO, fontSize: '0.8rem', color: '#fbbf24' }}>
                          Provisional{Number.isFinite(s.daily.confidencePct) ? ` · ${Math.round(s.daily.confidencePct)}%` : ''}
                        </Typography>
                      )}
                      {!s.daily && (
                        <Typography variant="body2" sx={{ color: MUTED }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{fmtPrice(s.daily?.pivotPrice)}</TableCell>
                    <TableCell align="right">{fmtPrice(s.price?.last)}</TableCell>
                    <TableCell align="right">
                      {s.price?.pctFromPivot === null || s.price?.pctFromPivot === undefined ? (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      ) : (
                        <Typography variant="body2" fontWeight={600} sx={{ color: Math.abs(s.price.pctFromPivot) <= PIVOT_GREEN_PCT ? '#22c55e' : 'var(--dwlf-ink-70)' }}>
                          {s.price.pctFromPivot > 0 ? '+' : ''}{s.price.pctFromPivot}%
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ScreenerFooter />
    </Container>
  );
};

export default CycleScreenerPage;
